import React, { useState, useCallback } from 'react';
import { MoreVertical, Paperclip } from 'lucide-react';
import { messagingAPI } from '../services/messaging';

/**
 * Single message in thread. Shows sender photo, name, timestamp, content, read receipt.
 * Supports delete action (per-message menu). Attachments use conversation-scoped or own download URLs.
 */
export default function MessageBubble({
  message,
  isOwn,
  onDelete,
  conversationId,
  currentUserName,
  currentUserPhoto,
}) {
  const [showMenu, setShowMenu] = useState(false);
  const {
    message_id,
    sender_name,
    sender_photo,
    text,
    attachments,
    created_at,
    read_by,
  } = message || {};

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const effectiveName = isOwn
    ? (sender_name || currentUserName || 'You')
    : (sender_name || 'Unknown');

  const effectivePhoto = isOwn
    ? (sender_photo || currentUserPhoto || '')
    : (sender_photo || '');

  const initials = effectiveName
    ? effectiveName.trim().split(/\s+/).map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const handleAttachmentClick = useCallback(
    async (a) => {
      const key = a.key || a;
      if (!key) return;
      try {
        const url = conversationId && !isOwn
          ? await messagingAPI.getAttachmentDownloadUrl(conversationId, key)
          : await messagingAPI.getOwnAttachmentDownloadUrl(key);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      } catch (e) {
        console.error('Attachment download error:', e);
      }
    },
    [conversationId, isOwn]
  );

  return (
    <div className={`flex items-start gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}>
      <div className="flex-shrink-0">
        {effectivePhoto ? (
          <img src={effectivePhoto} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
            <span className="text-gray-600 font-semibold text-xs">{initials}</span>
          </div>
        )}
      </div>
      <div className={`flex-1 min-w-0 flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm text-gray-900">{isOwn ? 'You' : effectiveName}</span>
          <span className="text-xs text-gray-500">{formatTime(created_at)}</span>
          {onDelete && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowMenu((s) => !s)}
                className="p-1 hover:bg-gray-100 rounded"
                aria-label="Message options"
              >
                <MoreVertical className="w-4 h-4 text-gray-500" />
              </button>
              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    aria-hidden="true"
                    onClick={() => setShowMenu(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[120px]">
                    <button
                      type="button"
                      onClick={() => {
                        onDelete?.(message_id);
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="mt-1 text-sm text-gray-700 break-words">{text}</div>
        {Array.isArray(attachments) && attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {attachments.map((a, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleAttachmentClick(a)}
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline"
              >
                <Paperclip className="w-3.5 h-3.5" />
                {a.name || 'Attachment'}
              </button>
            ))}
          </div>
        )}
        {read_by?.length > 0 && isOwn && (
          <span className="text-xs text-gray-400 mt-1">Seen</span>
        )}
      </div>
    </div>
  );
}
